import React, { useState, useEffect } from 'react';
import VariantButton from '../VariantButton';
import Variants from '../Variants';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import './index.css';

function ProductDetail({ match, client, checkout, handleCheckout }) {
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addToCartText, setAddToCartText] = useState('Add To Bag');
  const [errorText, setErrorText] = useState('');
  // Custom attributes for line items - stores "name" and "number" attributes (only for custom variants)
  const [customAttributes, setCustomAttributes] = useState([]);
  const { width } = useWindowDimensions();

  useEffect(() => {
    if (client) {
      client.product.fetchByHandle(match.params.id).then((product) => {
        setProduct(product);
      });
    }
  }, []);

  function renderProductCover() {
    if (product.images.length === 0) { 
      const videoSrc = product.description.split('[/video]')[0].split('[video]')[1];
      return (
        <video style={{ width: '95%', margin: '0 auto' }} loop autoPlay muted playsInline>
          <source src={videoSrc} type="video/mp4" />
        </video>
      )
    } else {
      return (
        <div>
          {product.images.map((image, index) => (
            <img 
              key={index}
              style={{ width: '100%', margin: '0 auto', marginBottom: index < product.images.length - 1 ? '16px' : '0' }} 
              alt={`product ${index + 1}`} 
              src={image.src} 
            />
          ))}
        </div>
      )
    }
  }

  function renderProductDescription() {
    let description = product.descriptionHtml.split('[/description]')[0].split('[description]')[1];
    return <div className="description" dangerouslySetInnerHTML={{ __html: description }} />;
  }

  function renderProductDescriptionHTML() {
    let description = product.descriptionHtml.split('[/description]')[1];
    return <div className="description" dangerouslySetInnerHTML={{ __html: description }} />;
  }

  function renderVariant() {
    if (product.variants.length === 1) {
      return <></>;
    } else {
      return (
        <div className="variants">
          <Variants
            variants={product.variants}
            handleSelected={handleSelected}
            currentlySelectedVariant={selectedVariant}
          />
        </div>
      );
    }
  }

  async function addLineItem() {
    setAddToCartText('Adding..');
    const checkoutId = checkout.id;
    
    // Build line item with conditional custom attributes
    const lineItem = {
      variantId: product.variants.length === 1 ? product.variants[0].id : selectedVariant,
      quantity: 1
    };
    
    // Only add custom attributes if the selected variant is a custom variant
    if (isCustomVariantSelected()) {
      lineItem.customAttributes = [
        { key: "name", value: customAttributes.find(attr => attr.key === "name")?.value || "" },
        { key: "number", value: customAttributes.find(attr => attr.key === "number")?.value || "" }
      ];
    }
    
    const itemToAdd = [lineItem];

    let newCheckout = await client.checkout.addLineItems(checkoutId, itemToAdd);
    handleCheckout(newCheckout);
    setAddToCartText('Added!');
    
    // Clear selected variants and custom attributes after successful add
    setSelectedVariant(null);
    setCustomAttributes([]);
    
    setTimeout(() => {
      setAddToCartText('Add To Bag');
    }, 1200);
  }

  async function handleAddLineItem() {
    if (product.variants.length === 1) {
      await addLineItem();
    } else if (!selectedVariant) {
      setErrorText('Please select a variant!');
    } else {
      if (selectedVariant) {
        await addLineItem();
      }
    }
  }

  function renderAddToCart() {
    // Check if product is available for sale
    if (!product.availableForSale) {
      return <button className="btn-sold-out">SOLD OUT</button>;
    }
    
    // If there are multiple variants, check the selected variant's availability
    if (product.variants.length > 1) {
      if (!selectedVariant) {
        return (
          <button onClick={() => handleAddLineItem()} className="btn">
            {addToCartText}
          </button>
        );
      }
      
      const selectedVariantObj = product.variants.find(v => v.id === selectedVariant);
      if (selectedVariantObj && !selectedVariantObj.available) {
        return <button className="btn-sold-out">SOLD OUT</button>;
      }
    }
    
    // If single variant, check its availability
    if (product.variants.length === 1 && !product.variants[0].available) {
      return <button className="btn-sold-out">SOLD OUT</button>;
    }
    
    return (
      <button onClick={() => handleAddLineItem()} className="btn">
        {addToCartText}
      </button>
    );
  }

  function handleSelected(variantId) {
    setErrorText('');
    setSelectedVariant(variantId);
    setCustomAttributes([]);
  }

  // Function to get the current price based on selected variant
  const getCurrentPrice = () => {
    if (product.variants.length === 1) {
      return product.variants[0].price.amount;
    } else if (selectedVariant) {
      const selectedVariantObj = product.variants.find(v => v.id === selectedVariant);
      return selectedVariantObj ? selectedVariantObj.price.amount : product.variants[0].price.amount;
    }
    return product.variants[0].price.amount;
  };

  // Function to check if the selected variant is a custom variant
  const isCustomVariantSelected = () => {
    if (product.variants.length === 1) {
      return product.variants[0].title.toLowerCase().includes('custom');
    } else if (selectedVariant) {
      const selectedVariantObj = product.variants.find(v => v.id === selectedVariant);
      return selectedVariantObj ? selectedVariantObj.title.toLowerCase().includes('custom') : false;
    }
    return false;
  };

  // Function to check if the selected custom variant is available and should show the form
  const shouldShowCustomForm = () => {
    if (product.variants.length === 1) {
      const variant = product.variants[0];
      return variant.title.toLowerCase().includes('custom') && variant.available;
    } else if (selectedVariant) {
      const selectedVariantObj = product.variants.find(v => v.id === selectedVariant);
      return selectedVariantObj ? 
        selectedVariantObj.title.toLowerCase().includes('custom') && selectedVariantObj.available : false;
    }
    return false;
  };

  // Helper function to set name attribute
  const setNameAttribute = (value) => {
    setCustomAttributes(prev => {
      const existing = prev.find(attr => attr.key === "name");
      if (existing) {
        return prev.map(attr => 
          attr.key === "name" ? { ...attr, value } : attr
        );
      }
      return [...prev, { key: "name", value }];
    });
  };

  // Helper function to handle name input changes - limits to 9 characters
  const handleNameInputChange = (e) => {
    const value = e.target.value;
    // Limit to 9 characters
    if (value.length <= 9) {
      setNameAttribute(value);
    }
  };

  // Helper function to set number attribute
  const setNumberAttribute = (value) => {
    setCustomAttributes(prev => {
      const existing = prev.find(attr => attr.key === "number");
      if (existing) {
        return prev.map(attr => 
          attr.key === "number" ? { ...attr, value } : attr
        );
      }
      return [...prev, { key: "number", value }];
    });
  };

  const handleNumberInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers (including empty string for clearing) and limit to 3 digits
    if (value === '' || (/^\d+$/.test(value) && value.length <= 3)) {
      setNumberAttribute(value);
    }
  };

  if (!product) return <div style={{ height: '70.1vh'}}></div>;

  return (
    <div className="detail-container">
      <div className="details">
        <div className="product-section">
          {renderProductCover()}
          {renderProductDescriptionHTML()}
        </div>
        <div className="info-section">
          <div className="info-section-sticky">
            <div className="info-title">
              {product.title.split('] ')[1]}
              <br />${parseFloat(getCurrentPrice()).toFixed(2)}
            </div>
            <div className="info-description">{renderProductDescription()}</div>
            <div className="info-body">
              <div className="separator" />
            </div>
            {renderVariant()}
            {shouldShowCustomForm() && (
              <div className="custom-attributes-container">
                <div className="attribute-input">
                  <label htmlFor="name-input">Name:</label>
                  <input
                    id="name-input"
                    type="text"
                    maxLength="9"
                    value={customAttributes.find(attr => attr.key === "name")?.value || ""}
                    onChange={handleNameInputChange}
                  />
                </div>
                <div className="attribute-input">
                  <label htmlFor="number-input">Number:</label>
                  <input
                    id="number-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={customAttributes.find(attr => attr.key === "number")?.value || ""}
                    onChange={handleNumberInputChange}
                  />
                </div>
              </div>
            )}
            <div className="button-container">{renderAddToCart()}</div>
            <div className="detail-error-message">{errorText}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
